import React, { useState, useEffect } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { FiClock, FiBriefcase, FiUser, FiFileText, FiHome, FiMoreHorizontal, FiMap, FiUsers, FiCalendar, FiX, FiBell, FiCheckCircle, FiPackage, FiShield, FiDollarSign, FiCheckSquare, FiBookOpen, FiAward, FiList, FiMail, FiSun, FiHash } from 'react-icons/fi';
import { useAuth } from '../contexts/AuthContext';
import { API_URL, API_BASE_URL } from '../config';
import { getAuthToken } from '../services/api';
import GlobalSearch from './GlobalSearch';
import axios from 'axios';

function MobileLayout({ children }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [showMoreMenu, setShowMoreMenu] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  
  const navItems = [
    { path: '/dashboard', icon: FiHome, label: 'Home' },
    { path: '/time-clock', icon: FiClock, label: 'Clock' },
    { path: '/jobs', icon: FiBriefcase, label: 'Jobs' },
    { path: '/schedule', icon: FiCalendar, label: 'Schedule' },
  ];

  const canManagePayroll = user?.can_manage_payroll || false;
  const canEditQuotes = user?.can_edit_quotes || false;
  const isRecognitionAdmin = ['Admin', 'Owner', 'Office Manager'].includes(user?.role);

  const moreItems = [
    { path: '/briefing', icon: FiSun, label: 'My Briefing' },
    { path: '/field-codes', icon: FiHash, label: 'Field Codes' },
    ...(canManagePayroll
      ? [{ path: '/billing', icon: FiDollarSign, label: 'Billing' }]
      : canEditQuotes
        ? [{ path: '/billing?tab=quotes', icon: FiFileText, label: 'Quotes' }]
        : []),
    { path: '/email-inbox', icon: FiMail, label: 'Email' },
    { path: '/directory', icon: FiBookOpen, label: 'Directory' },
    { path: '/todos', icon: FiList, label: 'To-Do' },
    { path: '/calendar', icon: FiCalendar, label: 'Calendar' },
    { path: '/timesheets', icon: FiFileText, label: 'Hours' },
    { path: '/map', icon: FiMap, label: 'Map View' },
    { path: '/supplies', icon: FiPackage, label: 'Supplies' },
    { path: '/safety', icon: FiShield, label: 'Safety' },
    { path: '/training', icon: FiBookOpen, label: 'Training' },
    { path: '/qc', icon: FiCheckSquare, label: 'QA / QC' },
    { path: '/nominations', icon: FiAward, label: 'Recognition' },
    ...(isRecognitionAdmin ? [{ path: '/nominations/admin', icon: FiAward, label: 'Recognition Admin' }] : []),
    { path: '/clients', icon: FiUsers, label: 'Clients' },
    { path: '/time-off', icon: FiFileText, label: 'Time Off' },
    { path: '/profile', icon: FiUser, label: 'Profile' },
  ];

  // Fetch notifications
  useEffect(() => {
    if (user) {
      fetchNotifications();
      const interval = setInterval(fetchNotifications, 30000); // Poll every 30 seconds
      return () => clearInterval(interval);
    }
  }, [user]);

  const getAuthConfig = async () => {
    const token = await getAuthToken();
    return {
      withCredentials: true,
      headers: token ? { Authorization: `Bearer ${token}` } : {}
    };
  };

  const fetchNotifications = async () => {
    try {
      const config = await getAuthConfig();
      const response = await axios.get(`${API_URL}/notifications`, config);
      const notifs = response.data.notifications || [];
      setNotifications(notifs);
      setUnreadCount(notifs.filter(n => !n.is_read).length);
    } catch (error) {
      console.error('Error fetching notifications:', error);
    }
  };

  const markAsRead = async (notificationId) => {
    // Update the list right away; the refetch below confirms it.
    if (notifications.some(n => n.id === notificationId && !n.is_read)) {
      setNotifications(prev => prev.map(n => (n.id === notificationId ? { ...n, is_read: true } : n)));
      setUnreadCount(prev => Math.max(0, prev - 1));
    }
    try {
      const config = await getAuthConfig();
      await axios.post(`${API_URL}/notifications/${notificationId}/read`, {}, config);
      fetchNotifications();
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  const markAllAsRead = async () => {
    try {
      const config = await getAuthConfig();
      await axios.post(`${API_URL}/notifications/read-all`, {}, config);
      fetchNotifications();
    } catch (error) {
      console.error('Error marking all as read:', error);
    }
  };

  const handleNotificationClick = (notification) => {
    markAsRead(notification.id);
    setShowNotifications(false);
    if (notification.related_job_id) {
      navigate(`/jobs/${notification.related_job_id}`);
    }
  };

  const formatTimeAgo = (dateStr) => {
    if (!dateStr) return '';
    let timestamp = dateStr;
    if (!timestamp.endsWith('Z') && !timestamp.includes('+')) {
      timestamp = timestamp + 'Z';
    }
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  const handleMoreItemClick = (path) => {
    setShowMoreMenu(false);
    navigate(path);
  };

  const isMoreActive = moreItems.some(item => location.pathname === item.path);

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      minHeight: '100vh',
      backgroundColor: '#f5f5f5'
    }}>
      {/* Header */}
      <header style={{
        backgroundColor: '#1976d2',
        color: 'white',
        padding: '15px 20px',
        paddingTop: 'max(15px, env(safe-area-inset-top))',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        position: 'sticky',
        top: 0,
        zIndex: 99970
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          {user?.profile_photo && (
            <img
              src={`${API_BASE_URL}${user.profile_photo}`}
              alt={user.name}
              style={{
                width: '34px',
                height: '34px',
                borderRadius: '50%',
                objectFit: 'cover',
                border: '2px solid rgba(255,255,255,0.5)'
              }}
            />
          )}
          <div>
            <h1 style={{ margin: 0, fontSize: '20px', fontWeight: '600' }}>
              SurveyPro
            </h1>
            {user && (
              <p style={{ margin: '2px 0 0', fontSize: '12px', opacity: 0.9 }}>
                {user.name}
              </p>
            )}
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
          {/* Notification Bell */}
          <button
            onClick={() => setShowNotifications(!showNotifications)}
            style={{
              position: 'relative',
              background: 'none',
              border: 'none',
              color: 'white',
              cursor: 'pointer',
              padding: '5px'
            }}
          >
            <FiBell size={24} />
            {unreadCount > 0 && (
              <span style={{
                position: 'absolute',
                top: 0,
                right: 0,
                backgroundColor: '#ef4444',
                color: 'white',
                borderRadius: '50%',
                width: '18px',
                height: '18px',
                fontSize: '11px',
                fontWeight: 'bold',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                {unreadCount > 9 ? '9+' : unreadCount}
              </span>
            )}
          </button>
          <div style={{ 
            fontSize: '14px',
            textAlign: 'right'
          }}>
            {new Date().toLocaleDateString('en-US', { 
              weekday: 'short', 
              month: 'short', 
              day: 'numeric' 
            })}
          </div>
        </div>
      </header>

      {/* Universal search (blue bar to match the header so the input reads correctly) */}
      <div style={{ backgroundColor: '#1976d2', paddingTop: 4 }}>
        <GlobalSearch />
      </div>

      {/* Notification Panel Overlay */}
      {showNotifications && (
        <div 
          onClick={() => setShowNotifications(false)}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            zIndex: 99990
          }}
        />
      )}

      {/* Notification Panel */}
      {showNotifications && (
        <div style={{
          position: 'fixed',
          top: 'max(70px, calc(env(safe-area-inset-top) + 60px))',
          left: '10px',
          right: '10px',
          maxHeight: '70vh',
          backgroundColor: 'white',
          borderRadius: '16px',
          boxShadow: '0 4px 20px rgba(0,0,0,0.25)',
          zIndex: 99995,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column'
        }}>
          {/* Header */}
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '15px',
            borderBottom: '1px solid #eee',
            backgroundColor: '#f8f9fa'
          }}>
            <span style={{ fontWeight: '600', fontSize: '16px' }}>
              Notifications {unreadCount > 0 && `(${unreadCount})`}
            </span>
            <div style={{ display: 'flex', gap: '10px' }}>
              {unreadCount > 0 && (
                <button
                  onClick={markAllAsRead}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: '#1976d2',
                    fontSize: '14px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px'
                  }}
                >
                  <FiCheckCircle size={16} />
                  Mark all read
                </button>
              )}
              <button 
                onClick={() => setShowNotifications(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  padding: '5px',
                  cursor: 'pointer'
                }}
              >
                <FiX size={20} />
              </button>
            </div>
          </div>

          {/* Notification List */}
          <div style={{ 
            overflowY: 'auto',
            flex: 1
          }}>
            {notifications.length === 0 ? (
              <div style={{
                padding: '40px 20px',
                textAlign: 'center',
                color: '#666'
              }}>
                <FiBell size={40} style={{ opacity: 0.3, marginBottom: '10px' }} />
                <p>No notifications</p>
              </div>
            ) : (
              notifications.map(notification => (
                <div
                  key={notification.id}
                  onClick={() => handleNotificationClick(notification)}
                  style={{
                    padding: '15px',
                    borderBottom: '1px solid #eee',
                    backgroundColor: notification.is_read ? 'white' : '#f0f9ff',
                    cursor: 'pointer'
                  }}
                >
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    gap: '10px'
                  }}>
                    <div style={{ flex: 1 }}>
                      <div style={{
                        fontWeight: notification.is_read ? '400' : '600',
                        fontSize: '14px',
                        marginBottom: '4px',
                        color: '#333'
                      }}>
                        {notification.title}
                      </div>
                      <div style={{
                        fontSize: '13px',
                        color: '#666',
                        marginBottom: '6px'
                      }}>
                        {notification.message}
                      </div>
                      <div style={{
                        fontSize: '11px',
                        color: '#999'
                      }}>
                        {formatTimeAgo(notification.created_at)}
                      </div>
                    </div>
                    {!notification.is_read && (
                      // Marks just this one read without opening the job.
                      <button
                        onClick={(e) => { e.stopPropagation(); markAsRead(notification.id); }}
                        aria-label="Mark as read"
                        style={{
                          flexShrink: 0,
                          minWidth: '44px',
                          minHeight: '44px',
                          margin: '-8px -6px 0 0',
                          background: 'none',
                          border: 'none',
                          color: '#1976d2',
                          cursor: 'pointer',
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '2px',
                          fontSize: '10px',
                          fontWeight: 600
                        }}
                      >
                        <FiCheckCircle size={20} />
                        Read
                      </button>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {/* Main Content */}
      <main style={{
        flex: 1,
        padding: '15px',
        paddingBottom: '80px',
        overflowY: 'auto'
      }}>
        {children}
      </main>

      {/* More Menu Overlay */}
      {showMoreMenu && (
        <div 
          onClick={() => setShowMoreMenu(false)}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            zIndex: 99990
          }}
        />
      )}

      {/* More Menu Bottom Sheet */}
      {showMoreMenu && (
        <div style={{
          position: 'fixed',
          bottom: 70,
          left: 10,
          right: 10,
          backgroundColor: 'white',
          borderRadius: '16px',
          padding: '10px',
          zIndex: 99995,
          boxShadow: '0 -4px 20px rgba(0,0,0,0.2)',
          display: 'flex',
          flexDirection: 'column',
          // Never extend under the status bar; cap height and let the list scroll
          maxHeight: 'calc(100vh - 90px - env(safe-area-inset-top))'
        }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '10px 10px 15px',
            borderBottom: '1px solid #eee',
            marginBottom: '10px'
          }}>
            <span style={{ fontWeight: '600', fontSize: '16px' }}>More Options</span>
            <button 
              onClick={() => setShowMoreMenu(false)}
              style={{
                background: 'none',
                border: 'none',
                padding: '5px',
                cursor: 'pointer'
              }}
            >
              <FiX size={20} />
            </button>
          </div>
          <div style={{ overflowY: 'auto', flex: 1, WebkitOverflowScrolling: 'touch' }}>
          {moreItems.map(item => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;
            return (
              <button
                key={item.path}
                onClick={() => handleMoreItemClick(item.path)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '15px',
                  width: '100%',
                  padding: '15px',
                  backgroundColor: isActive ? '#e3f2fd' : 'transparent',
                  border: 'none',
                  borderRadius: '10px',
                  cursor: 'pointer',
                  textAlign: 'left',
                  fontSize: '16px',
                  color: isActive ? '#1976d2' : '#333'
                }}
              >
                <Icon size={22} />
                {item.label}
              </button>
            );
          })}
          </div>
        </div>
      )}

      {/* Bottom Navigation */}
      <nav style={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        backgroundColor: 'white',
        borderTop: '1px solid #e0e0e0',
        display: 'flex',
        justifyContent: 'space-around',
        paddingBottom: 'max(10px, env(safe-area-inset-bottom))',
        paddingTop: '8px',
        zIndex: 99980,
        boxShadow: '0 -2px 10px rgba(0,0,0,0.1)'
      }}>
        {navItems.map(item => {
          const Icon = item.icon;
          const isActive = location.pathname === item.path || 
            (item.path === '/dashboard' && location.pathname === '/');
          
          return (
            <NavLink
              key={item.path}
              to={item.path}
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                textDecoration: 'none',
                color: isActive ? '#1976d2' : '#666',
                padding: '5px 15px',
                transition: 'color 0.2s'
              }}
            >
              <Icon size={24} />
              <span style={{ 
                fontSize: '11px', 
                marginTop: '4px',
                fontWeight: isActive ? '600' : '400'
              }}>
                {item.label}
              </span>
            </NavLink>
          );
        })}
        
        {/* More Button */}
        <button
          onClick={() => setShowMoreMenu(!showMoreMenu)}
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            background: 'none',
            border: 'none',
            color: isMoreActive || showMoreMenu ? '#1976d2' : '#666',
            padding: '5px 15px',
            cursor: 'pointer',
            transition: 'color 0.2s'
          }}
        >
          <FiMoreHorizontal size={24} />
          <span style={{ 
            fontSize: '11px', 
            marginTop: '4px',
            fontWeight: isMoreActive || showMoreMenu ? '600' : '400'
          }}>
            More
          </span>
        </button>
      </nav>
    </div>
  );
}

export default MobileLayout;